import { useCallback } from 'react'

import FeedbackMessage from '@space-core/components/form/feedbackMessage'

import CustomInput from '@space-shell/common/components/customInput'
import { useForm } from '@space-shell/common/hooks/useForm'
import { useTranslation } from 'react-i18next'

import { formFields, formFieldsValidator } from './constants'
import { EmojiSelect } from './emojiSelect'

import { useFieldState } from './useFieldState'

import type { ExternalLinkFormFields } from '../externalLinkModal'

const URL_EXAMPLE = 'https://hotmart.com'

const ExternalLinkForm = () => {
  const { t } = useTranslation()
  const { data: formData, setFormData } = useForm<ExternalLinkFormFields>()
  const { fieldsState, setFieldsState } = useFieldState(formData)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    if (value?.length > fieldsState[name].maxSize) {
      return
    }

    setFieldsState((current) => {
      const newState = { ...current }

      newState[name].currentSize = value.length
      newState[name].isTouched = true

      return newState
    })

    setFormData(name as keyof ExternalLinkFormFields, value)
  }

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name } = event.target

    if (formFields[name].isTouched) return

    setFieldsState((current) => {
      const newState = { ...current }

      newState[name].isTouched = true

      return newState
    })
  }

  const handleEmojiSelect = (payload: string) => {
    setFormData(formFields.icon.name as keyof ExternalLinkFormFields, payload)
  }

  const hasError = useCallback(
    (name: string) =>
      fieldsState[name]?.isTouched && !formFieldsValidator[name].isValid(formData[name].value),
    [fieldsState, formData]
  )

  return (
    <div className='_d-flex _flex-column'>
      <div className='_mb-5 _d-flex'>
        <div className='_mr-2'>
          <EmojiSelect
            name={formFields.icon.name}
            label={t('external_links.form.icon.label')}
            onChange={handleEmojiSelect}
            value={formData.icon.value}
          />
        </div>
        <div className='_flex-1'>
          <CustomInput
            label={t('external_links.form.name.label')}
            aria-label={t('external_links.form.name.label')}
            name={formFields.name.name}
            onChange={handleChange}
            onBlur={handleBlur}
            value={formData.name.value}
            placeholder={t('external_links.form.name.placeholder')}
            maxSize={fieldsState.name?.maxSize}
            currentSize={fieldsState.name?.currentSize}
            hasError={hasError(formFields.name.name)}
          />
        </div>
      </div>
      <div>
        <CustomInput
          label={t('external_links.form.url.label')}
          aria-label={t('external_links.form.url.label')}
          name={formFields.url.name}
          onChange={handleChange}
          onBlur={handleBlur}
          value={formData.url.value}
          placeholder={t('external_links.form.url.placeholder', {
            url: URL_EXAMPLE
          })}
          hasError={hasError(formFields.url.name)}
        />
        {hasError(formFields.url.name) && (
          <FeedbackMessage error={t('external_links.messages.invalid_url')} />
        )}
      </div>
    </div>
  )
}

export default ExternalLinkForm
